import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { morphSection } from '@theme/section-renderer';

const CART_DRAWER_SECTION_ID = 'cart-drawer-section';

/**
 * Keep the summary outside the scrollable item pane instead of letting a sticky summary
 * physically cover the last quantity controls. This is injected once so it also applies
 * after repeated Section Rendering API morphs.
 */
function installStableDrawerLayout() {
  if (document.getElementById('daymerit-cart-drawer-layout')) return;

  const style = document.createElement('style');
  style.id = 'daymerit-cart-drawer-layout';
  style.textContent = `
    #cart-drawer .cart-drawer__content:has(.cart-form) {
      overflow: hidden !important;
      min-height: 0;
    }
    #cart-drawer .cart-drawer__content:has(.cart-form) > .cart-drawer__items {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto !important;
      overscroll-behavior: contain;
      padding-block-end: max(var(--gap-lg), 12px);
    }
    #cart-drawer .cart-drawer__summary {
      position: relative !important;
      inset-block-end: auto !important;
      bottom: auto !important;
      flex: 0 0 auto;
      margin-block-start: 0;
      mask-image: none !important;
      -webkit-mask-image: none !important;
    }
    #cart-drawer .theme-drawer__dialog[cart-summary-sticky='false'] .cart-drawer__items {
      overflow-y: auto !important;
    }
  `;
  document.head.appendChild(style);
}

class CartDrawerComponent extends Component {
  /** @type {Promise<void> | null} */
  #consistencyPromise = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    installStableDrawerLayout();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    if (this.#themeDrawer?.hasAttribute('open')) this.#handleDrawerOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #handleDrawerOpen = () => {
    this.#ensureDrawerConsistency()
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Consistency check failed:', error);
      })
      .finally(() => {
        if (this.isConnected) this.#updateStickyState();
      });

    customElements.whenDefined('shopify-payment-terms').then(() => {
      if (!this.isConnected) return;
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add';
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError) return;

        // DAYMERIT writers resolve only after their exact mutation response has already
        // been rendered. Running a second section render here can cancel/replace that
        // render, so consistency is only needed for native/external writers.
        if (!detail?.uiSynced) await this.#ensureDrawerConsistency();
        if (!this.isConnected) return;

        const openAndSettle = () => {
          if (!this.isConnected) return;
          if (shouldAutoOpen && !this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          requestAnimationFrame(() => {
            if (this.isConnected) this.#updateStickyState();
          });
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Cart event rejected:', error);
      });
  };

  /**
   * Compare visible drawer state to Shopify's no-cache Ajax cart. Only a mismatch performs
   * a fresh, no-store section fetch. This is a guardrail, not part of the normal mutation path.
   */
  async #ensureDrawerConsistency() {
    if (this.#consistencyPromise) return this.#consistencyPromise;

    this.#consistencyPromise = (async () => {
      if (!this.isConnected) return;

      const cartItems = /** @type {any} */ (this.querySelector('cart-items-component[data-drawer]'));
      if (!cartItems) return;

      await customElements.whenDefined('cart-items-component');
      if (!this.isConnected) return;

      const cart = await cartItems.fetchCartData();
      if (!this.isConnected) return;

      const actualCount = Number(
        cart?.item_count ?? cart?.items?.reduce((sum, item) => sum + Number(item.quantity || 0), 0) ?? 0
      );

      const hiddenCountText = cartItems.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      const renderedCount = hiddenCountText ? Number(hiddenCountText) : 0;
      const visuallyEmpty =
        cartItems.querySelector('[data-cart-drawer-empty]') !== null || cartItems.querySelector('.cart-form') === null;

      const mismatch =
        actualCount === 0
          ? !visuallyEmpty
          : visuallyEmpty || !Number.isFinite(renderedCount) || renderedCount !== actualCount;

      if (!mismatch) return;
      await this.#renderFreshDrawerSection();
    })().finally(() => {
      this.#consistencyPromise = null;
    });

    return this.#consistencyPromise;
  }

  async #renderFreshDrawerSection() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', CART_DRAWER_SECTION_ID);
    url.searchParams.sort();

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) throw new Error(`Cart drawer refresh failed: ${response.status}`);
    const html = await response.text();
    await morphSection(CART_DRAWER_SECTION_ID, html, { mode: 'full' });
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // The DAYMERIT drawer now uses a dedicated scroll pane for items and a non-overlapping
    // summary pane, so this attribute is retained only for compatibility with theme CSS.
    dialog.setAttribute('cart-summary-sticky', 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
